import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../layouts/PageTitle";
import { selectUser } from "../../../store/authSlice";
import {
  appApi,
  useGetSalesCustomersQuery,
  useGetSalesQuery,
  useGetSalesSellersQuery,
} from "../../../store/appApi";
import {
  createSaleApi,
  updateSaleApi,
  cancelSaleApi,
} from "../../../services/sales";

const panel =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";
const field =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";
const ghostBtn =
  "rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:border-amber-400 hover:text-amber-700";
const WHITE = { backgroundColor: "#fff", color: "#1f2937" };

const todayInput = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";
const errMsg = (e) => e?.response?.data?.message || "Something went wrong.";

const STATUS_TONE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-gray-100 text-gray-500 border-gray-200",
};
const Badge = ({ tone, children }) => (
  <span
    className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
      tone || "bg-gray-100 text-gray-600 border-gray-200"
    }`}
  >
    {children}
  </span>
);

const Sales = () => {
  const dispatch = useDispatch();
  const me = useSelector(selectUser);
  const isAdmin = me?.role === "super-admin";
  const [view, setView] = useState("customers"); // customers | sales
  const [employeeId, setEmployeeId] = useState("");

  const { data: sellersData } = useGetSalesSellersQuery(undefined, {
    skip: !isAdmin,
  });
  const sellers = sellersData?.sellers || [];

  const custQ = useGetSalesCustomersQuery(
    { employeeId: isAdmin ? employeeId : "" },
    { skip: view !== "customers" || (isAdmin && !employeeId) },
  );
  const customers = useMemo(
    () => custQ.data?.customers || [],
    [custQ.data],
  );
  const sourceReportId = custQ.data?.sourceReportId || null;

  const [salesFilter, setSalesFilter] = useState("");
  const [q, setQ] = useState("");
  const [qLive, setQLive] = useState("");
  const [page, setPage] = useState(1);
  const salesQ = useGetSalesQuery(
    { status: salesFilter, q: qLive, page },
    { skip: view !== "sales" },
  );
  const sales = salesQ.data?.sales || [];
  const totalPages = salesQ.data?.totalPages || 1;

  const refresh = () => dispatch(appApi.util.invalidateTags(["Sales"]));

  // ── Mark-as-sold inline draft, keyed by customer id ──────────────────────
  const [draft, setDraft] = useState(null); // { customerId, saleDate, amount, projectName, unit, remarks }
  const [saving, setSaving] = useState(false);
  const openDraft = (c) =>
    setDraft({
      customerId: c.id,
      saleDate: todayInput(),
      amount: "",
      projectName: "",
      unit: "",
      remarks: "",
    });
  const patchDraft = (p) => setDraft((d) => ({ ...d, ...p }));

  const saveSale = async () => {
    if (!draft || !sourceReportId) return;
    try {
      setSaving(true);
      await createSaleApi({
        sourceReportId,
        customerId: draft.customerId,
        salesPersonId: isAdmin ? employeeId || undefined : undefined,
        saleDate: draft.saleDate,
        amount: draft.amount || undefined,
        projectName: draft.projectName,
        unit: draft.unit,
        remarks: draft.remarks,
      });
      setDraft(null);
      refresh();
      Swal.fire({
        icon: "success",
        title: "Marked as sold",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Couldn't mark sold", text: errMsg(e) });
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (s, status) => {
    try {
      await updateSaleApi(s._id, { status });
      refresh();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed", text: errMsg(e) });
    }
  };
  const cancel = async (s) => {
    const ok = await Swal.fire({
      icon: "warning",
      title: "Cancel this sale?",
      text: "The customer is re-enabled in the database.",
      showCancelButton: true,
      confirmButtonText: "Cancel sale",
    });
    if (!ok.isConfirmed) return;
    try {
      await cancelSaleApi(s._id);
      refresh();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed", text: errMsg(e) });
    }
  };

  const soldCount = useMemo(
    () => customers.filter((c) => c.sold).length,
    [customers],
  );

  return (
    <>
      <PageTitle activeMenu={view === "customers" ? "Customer Database" : "Sales"} motherMenu="Sales" />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-full bg-gray-100 p-1">
          {[
            ["customers", "Customer Database"],
            ["sales", "Sales"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setView(k)}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
                view === k ? "bg-white text-gray-900 shadow" : "text-gray-500"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {view === "customers" && isAdmin && (
          <select
            className={`${field} w-64`}
            style={WHITE}
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
          >
            <option value="" style={WHITE}>
              — pick a sales / tele-calling employee —
            </option>
            {sellers.map((u) => (
              <option key={u._id} value={u._id} style={WHITE}>
                {u.name} · {u.role}
              </option>
            ))}
          </select>
        )}
        {view === "customers" && customers.length > 0 && (
          <span className="text-xs text-gray-400">
            {customers.length} customers · {soldCount} sold
          </span>
        )}
      </div>

      {view === "customers" ? (
        <section className={panel}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 text-left">Customer</th>
                  <th className="px-4 py-3 text-left">Phone</th>
                  <th className="px-4 py-3 text-left">Lead</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">Budget</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {custQ.isFetching ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                      Loading…
                    </td>
                  </tr>
                ) : isAdmin && !employeeId ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                      Pick an employee to see their customer database.
                    </td>
                  </tr>
                ) : customers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                      No customers on this roster.
                    </td>
                  </tr>
                ) : (
                  customers.map((c) => (
                    <React.Fragment key={c.id}>
                      <tr
                        className={
                          c.sold ? "bg-gray-50 opacity-60" : "hover:bg-amber-50/30"
                        }
                      >
                        <td className="px-4 py-2.5 font-medium text-gray-800">
                          {c.name || "—"}
                          {c.sold && (
                            <span className="ml-2 align-middle">
                              <Badge tone="bg-emerald-50 text-emerald-700 border-emerald-200">
                                Sold
                              </Badge>
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">
                          {c.phone || "—"}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">
                          {c.lead || "—"}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">
                          {c.propertyType || "—"}
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">
                          {c.budget || "—"}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {c.sold ? (
                            <span className="text-xs text-gray-400">
                              {c.sale?.salesPersonId?.name || c.sale?.salesPersonName}
                              {c.sale?.saleDate ? ` · ${fmtDate(c.sale.saleDate)}` : ""}
                            </span>
                          ) : (
                            <button
                              className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-slate-950 transition hover:bg-amber-400"
                              onClick={() => openDraft(c)}
                            >
                              Mark as Sold
                            </button>
                          )}
                        </td>
                      </tr>
                      {draft?.customerId === c.id && (
                        <tr className="bg-amber-50/40">
                          <td colSpan={6} className="px-4 py-3">
                            <div className="flex flex-wrap items-end gap-2">
                              <label className="text-xs text-gray-500">
                                Sale date
                                <input
                                  type="date"
                                  className={`${field} mt-0.5 w-40`}
                                  style={WHITE}
                                  max={todayInput()}
                                  value={draft.saleDate}
                                  onChange={(e) =>
                                    patchDraft({ saleDate: e.target.value })
                                  }
                                />
                              </label>
                              <label className="text-xs text-gray-500">
                                Amount (optional)
                                <input
                                  className={`${field} mt-0.5 w-32`}
                                  style={WHITE}
                                  inputMode="numeric"
                                  value={draft.amount}
                                  onChange={(e) =>
                                    patchDraft({ amount: e.target.value })
                                  }
                                />
                              </label>
                              <label className="text-xs text-gray-500">
                                Project
                                <input
                                  className={`${field} mt-0.5 w-40`}
                                  style={WHITE}
                                  value={draft.projectName}
                                  onChange={(e) =>
                                    patchDraft({ projectName: e.target.value })
                                  }
                                />
                              </label>
                              <label className="text-xs text-gray-500">
                                Unit
                                <input
                                  className={`${field} mt-0.5 w-28`}
                                  style={WHITE}
                                  value={draft.unit}
                                  onChange={(e) =>
                                    patchDraft({ unit: e.target.value })
                                  }
                                />
                              </label>
                              <label className="min-w-[12rem] flex-1 text-xs text-gray-500">
                                Remarks
                                <input
                                  className={`${field} mt-0.5`}
                                  style={WHITE}
                                  value={draft.remarks}
                                  onChange={(e) =>
                                    patchDraft({ remarks: e.target.value })
                                  }
                                />
                              </label>
                              <button
                                className="rounded-lg bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-emerald-500 disabled:opacity-50"
                                disabled={saving}
                                onClick={saveSale}
                              >
                                {saving ? "…" : "Save & transfer to Sales"}
                              </button>
                              <button
                                className={ghostBtn}
                                onClick={() => setDraft(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <section className={panel}>
          <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-5 py-3">
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                ["", "Live"],
                ["pending", "Pending"],
                ["confirmed", "Confirmed"],
                ["cancelled", "Cancelled"],
              ].map(([k, label]) => (
                <button
                  key={k || "live"}
                  onClick={() => {
                    setSalesFilter(k);
                    setPage(1);
                  }}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                    salesFilter === k
                      ? "bg-amber-500 text-slate-950"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <form
              className="ml-auto flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setQLive(q.trim());
                setPage(1);
              }}
            >
              <input
                className={`${field} w-56`}
                style={WHITE}
                placeholder="Customer / project / salesperson"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <button className={ghostBtn} type="submit">
                Search
              </button>
            </form>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 text-left">Customer</th>
                  <th className="px-4 py-3 text-left">Salesperson</th>
                  <th className="px-4 py-3 text-left">Sale date</th>
                  <th className="px-4 py-3 text-left">Amount</th>
                  <th className="px-4 py-3 text-left">Project / Unit</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {salesQ.isFetching ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                      Loading…
                    </td>
                  </tr>
                ) : sales.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                      No sales yet — mark a customer sold from the Customer Database.
                    </td>
                  </tr>
                ) : (
                  sales.map((s) => (
                    <tr key={s._id} className="hover:bg-amber-50/30">
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-gray-800">
                          {s.customerName || "—"}
                        </div>
                        <div className="text-xs text-gray-400">
                          {s.customerPhone || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-gray-700">
                        {s.salesPersonId?.name || s.salesPersonName || "—"}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">
                        {fmtDate(s.saleDate)}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600 tabular-nums">
                        {s.amount != null ? `₹${Number(s.amount).toLocaleString("en-IN")}` : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-600">
                        {s.projectName || "—"}
                        {s.unit ? ` · ${s.unit}` : ""}
                        {s.remarks ? (
                          <div className="text-gray-400">{s.remarks}</div>
                        ) : null}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <Badge tone={STATUS_TONE[s.status]}>{s.status}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex gap-1">
                          {s.status === "pending" && (
                            <button
                              className={ghostBtn}
                              onClick={() => setStatus(s, "confirmed")}
                            >
                              Confirm
                            </button>
                          )}
                          {s.status !== "cancelled" && (
                            <button
                              className={ghostBtn}
                              onClick={() => cancel(s)}
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-5 py-3 text-xs text-gray-500">
            <span>{salesQ.data?.totalCount ?? 0} total</span>
            <div className="flex items-center gap-2">
              <button
                className={ghostBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Prev
              </button>
              <span>
                {page} / {totalPages}
              </span>
              <button
                className={ghostBtn}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default Sales;
